// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createSelector } from '@reduxjs/toolkit';
import { getInvitationStatus } from '../user/userSelectors';

export const selectCompany = state => state.companies.company;
export const selectCompanyUuid = state => state.companies.company?.company_uuid;
export const selectCompanyEquipmentTypes = state =>
  state.companies?.company?.company_equipment_types || [];
export const selectCompanyEquipment = state => state.companies?.company?.company_equipment || [];
const selectSearchFilter = state => state.companies.searchFilter;
const selectCompanyMember = state => state.companies.companyMember;
const selectUser = state => state.user.user;

export const selectMemberRole = createSelector(
  [selectCompanyMember, selectCompany],
  (companyMember, company) => {
    if (!companyMember || !company) {
      return null;
    }

    return companyMember.user?.company_memberships.find(
      membership => membership.company_uuid === company.company_uuid
    );
  }
);

export const selectIsCompanyOwner = createSelector(
  [selectCompanyMember, selectCompany],
  (companyMember, company) => {
    if (!companyMember || !company) {
      return false;
    }

    return companyMember.user?.owned_companies.some(
      ownedCompany => ownedCompany.company_uuid === company.company_uuid
    );
  }
);

export const selectIsSelf = createSelector(
  [selectUser, selectCompanyMember],
  (user, companyMember) => !!user && !!companyMember && user.user_id === companyMember.user_id
);

const topicMap = {
  members: 'members',
  invitations: 'invites',
  studios: 'studio',
  providers: 'provider',
  equipment: 'company_equipment',
  keywords: 'keyword',
};

// Utility preserved exactly as in your original logic
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
};

export const selectFilteredCompany = createSelector(
  [selectCompany, selectSearchFilter],
  (company, searchFilter) => {
    if (!company) {
      return company;
    }

    const result = { ...company };

    const key = searchFilter?.topic ? topicMap[searchFilter.topic] : null;
    const items = key && Array.isArray(company[key]) ? company[key] : null;

    if (!items) {
      return result;
    }

    let filtered = [...items];

    // text search
    if (searchFilter?.query && searchFilter.searchBy) {
      const { searchBy, query } = searchFilter;
      filtered = filtered.filter(item => {
        const value = getNestedValue(item, searchBy);
        return value?.toString().toLowerCase().includes(query.toLowerCase());
      });
    }

    // filter by type/value
    if (searchFilter?.filterType && searchFilter?.filterValue) {
      const { filterType, filterValue, topic } = searchFilter;

      if (topic === 'members' && filterType === 'company_role') {
        filtered = filtered.filter(m => m.company_role === filterValue);
      }

      if (topic === 'invitations' && filterType === 'invitation_status') {
        filtered = filtered.filter(inv => getInvitationStatus(inv).status === filterValue);
      }
    }

    // Return updated derived company object
    return {
      ...company,
      [key]: filtered,
    };
  }
);

// Derived selector for studio credential message
export const selectNewStudioMessage = state => {
  const studios = state.companies?.company?.studio;
  if (!studios?.length) {
    return null;
  }

  const studioWithCredentials = studios.find(
    studio => studio.studio_oauth_app_client_id && studio.studio_oauth_app_client_secret
  );

  if (studioWithCredentials) {
    return (
      `${studioWithCredentials.name} successfully created.\n\n` +
      `Please copy the client ID:\n` +
      `${studioWithCredentials.studio_oauth_app_client_id}\n` +
      `and client secret:\n` +
      `${studioWithCredentials.studio_oauth_app_client_secret} now.\n` +
      `You won't be able to see them again!`
    );
  }

  return null;
};
